<template>
  <div>
    <h2>{{ $t('ARTWORK_UPLOAD.SIZE_HEADING') }}</h2>
    <v-form @submit.prevent="submit" autocomplete="off">
      <v-text-field
        :label="$t('ARTWORK_UPLOAD.ARTWORK_WEIGHT')"
        :hint="$t('ARTWORK_UPLOAD.ARTWORK_WEIGHT_HINT')"
        :error-messages="errors.weight"
        persistent-hint
        type="number"
        v-model="form.weight"
        @focus="selectOnFocus"
      />

      <!-- Size section -->
      <div class="my-4">
        <div>
          <h3 class="d-inline-block mr-3">{{ $t('ARTWORK_UPLOAD.ARTWORK_DIMENSION') }}</h3>
          <v-tooltip dark bottom max-width="300px">
            <template v-slot:activator="{ on }">
              <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
            </template>
            <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.DIMENSION') }}</span>
          </v-tooltip>
        </div>
        <v-layout row wrap>
          <v-flex>
            <v-text-field
              :label="$t('ARTWORK_UPLOAD.HEIGHT')"
              :hint="$t('ARTWORK_UPLOAD.HEIGHT_HINT')"
              :error-messages="errors.height"
              persistent-hint
              type="number"
              v-model="form.height"
              @focus="selectOnFocus"
            />
          </v-flex>
          <v-flex>
            <v-text-field
              :label="$t('ARTWORK_UPLOAD.WIDTH')"
              :hint="$t('ARTWORK_UPLOAD.WIDTH_HINT')"
              :error-messages="errors.width"
              persistent-hint
              type="number"
              v-model="form.width"
              @focus="selectOnFocus"
            />
          </v-flex>
          <v-flex>
            <v-text-field
              :label="$t('ARTWORK_UPLOAD.DEPTH')"
              :hint="$t('ARTWORK_UPLOAD.DEPTH_HINT')"
              :error-messages="errors.depth"
              persistent-hint
              type="number"
              v-model="form.depth"
              @focus="selectOnFocus"
            />
          </v-flex>
        </v-layout>
      </div>

      <!-- Pricing section -->
      <div class="my-5">
        <h2>{{ $t('ARTWORK_UPLOAD.PRICING') }}</h2>

        <!-- VAT dropdown -->
        <v-select
          :items="vat_rates"
          :label=" $t('ARTWORK_UPLOAD.VAT_LIABILITY')"
          :error-messages="errors.vat_rate"
          v-model="form.vat_rate"
          item-text="label"
          item-value="value"
          hide-details
          @change="calculateVatPrice"
        >
          <template v-slot:append-outer>
            <v-tooltip dark bottom max-width="300px">
              <template v-slot:activator="{ on }">
                <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
              </template>
              <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.VAT_LIABILITY') }}</span>
            </v-tooltip>
          </template>
        </v-select>

        <v-layout row wrap>
          <!-- Artwork price -->
          <v-flex>
            <v-text-field
              type="number"
              :label="$t('ARTWORK_UPLOAD.ARTWORK_PRICE')"
              :error-messages="errors.price"
              v-model="form.price"
              @input="calculateVatPrice"
              @focus="selectOnFocus"
              @blur="appendDecimals"
            >
              <template v-slot:append>
                <v-icon size="18">fas fa-euro-sign</v-icon>
              </template>
            </v-text-field>
          </v-flex>

          <!-- Artwork price with vat -->
          <v-flex>
            <v-text-field
              :label="$t('ARTWORK_UPLOAD.ARTTWORK_PRICE_WITH_VAT')"
              type="number"
              v-model="priceWithVat"
              @input="calculatePrice"
              @focus="selectOnFocus"
              @blur="appendDecimals"
            >
              <template v-slot:append>
                <v-icon size="18">fas fa-euro-sign</v-icon>
              </template>
              <template v-slot:append-outer>
                <v-tooltip dark bottom max-width="300px">
                  <template v-slot:activator="{ on }">
                    <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
                  </template>
                  <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.PRICE_WITH_VAT') }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-flex>
        </v-layout>

        <!-- Buyer price  -->
        <v-text-field box v-model="buyerPrice" :label="$t('ARTWORK_UPLOAD.BUYER_PRICE')" readonly>
          <template v-slot:append>
            <v-icon size="18">fas fa-euro-sign</v-icon>
          </template>
          <template v-slot:append-outer>
            <v-tooltip dark bottom max-width="300px">
              <template v-slot:activator="{ on }">
                <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
              </template>
              <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.BUYER_PRICE') }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </div>

      <v-layout row wrap mt-4>
        <v-flex shrink>
          <tk-btn flat @click="$emit('prev')">{{ $t('COMMON.BACK') }}</tk-btn>
        </v-flex>

        <v-flex grow text-xs-right>
          <tk-btn :loading="saving" flat @click="save(true)">{{ $t('COMMON.SAVE_RETURN') }}</tk-btn>
          <tk-btn :loading="saving" color="primary" @click="save(false)">{{ $t('COMMON.CONTINUE') }}</tk-btn>
        </v-flex>
      </v-layout>
    </v-form>
  </div>
</template>

<script>
import { Artwork } from "@/services/artwork/Artwork";
import { RepositoryFactory } from "@/services/RepositoryFactory";

const artworksRepo = RepositoryFactory.artworks();

export default {
  props: ["artwork"],

  data() {
    return {
      form: {
        vat_rate: 0,
        price: 0
      },
      priceWithVat: 0,
      saving: false,
      vat_rates: [
        {label: "0%", value: 0},
        {label: "14%", value: 14},
        {label: "25,5%", value: 25.5}
      ],
      errors: {}
    };
  },

  computed: {
    artworkAdditionalPrice() {
      const height = Number(this.form.height);
      const width = Number(this.form.width);
      const heightWithWidth = height + width;
      const weight = Number(this.form.weight);

      switch (true) {
        case (weight > 30000 || heightWithWidth > 330): // XXL
          return 180;

        case (height > 120 || width > 120): // XL
          return 140;

        case (height > 90 || width > 90): // L
          return 100;

        case (height > 40 || width > 40): // M
          return 60;

        case (height <= 40 && width <= 40): // S
          return 40;

        default:
          return 40;
      }
    },

    buyerPrice() {
      if (this.priceWithVat === 0) return 0;

      return Number(
        Number(this.priceWithVat) + Number(this.artworkAdditionalPrice)
      ).toFixed(2).replace('.', ',');
    }
  },

  created() {
    this.form = {...this.form, ...this.artwork};
    this.calculateVatPrice();
    this.appendDecimals();
  },

  methods: {
    selectOnFocus(e) {
      if (e && e.target.value) {
        e.target.select();
      }
    },

    async save(saveAndReturn) {
      this.saving = true;
      this.errors = {};

      try {
        await artworksRepo.createArtwork(
          this.artwork.id,
          3,
          saveAndReturn,
          this.form
        );

        if (saveAndReturn) {
          this.$router.push({name: "profile.artworks"});
        } else {
          this.$emit("next", "step3");
        }
      } catch (error) {
        if (error.status === 422 && error.data) {
          this.errors = error.data.errors;
        }

        this.$emit("error", "step3");
      }
      this.saving = false;
    },

    calculatePrice() {
      if (this.priceWithVat > 0) {
        if (this.form.vat_rate === 0) {
          // If VAT rate is 0 then price and price + VAT is equal.
          this.form.price = this.priceWithVat;
        }

        const vatAmount = this.priceWithVat / (this.form.vat_rate / 100 + 1);
        this.form.price = Number(vatAmount).toFixed(2);
      }
    },

    calculateVatPrice() {
      const vatAmount = (this.form.price * this.form.vat_rate) / 100;
      let price = Number(this.form.price) + vatAmount;

      const [wholeAmount, centAmount] = price.toFixed(2).split('.');

      /**
       * Do not allow decimals with only one fraction below or above the whole amount.
       * This workaround provides a fix for VAT rounding issues to keep the whole price
       * amounts without "weird" decimals. This is implemented on backend side too.
       *
       * @see app/Services/ArtworkService.php
       */

      if (centAmount) {
        if (centAmount === '01') {
          price = Math.floor(price);
        } else if (centAmount === '99') {
          price = Math.ceil(price);
        }
      }

      this.priceWithVat = Number(price).toFixed(2);
    },

    appendDecimals() {
      if (this.form.price) {
        this.form.price = Number(this.form.price).toFixed(2);
      }

      if (this.priceWithVat) {
        this.priceWithVat = Number(this.priceWithVat).toFixed(2);
      }
    },
  }
};
</script>
